import { useQuery } from "@apollo/client";
import Projects from "entities/ProjectList";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiPlus } from "react-icons/bi";
import { FaSearch } from "react-icons/fa";
import { TbFileExport } from "react-icons/tb";
import { useSearchParams } from "react-router-dom";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import Header from "shared/components/Header";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { useLazyQuery } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { QUALIFIERS } from "utils/api/graphql/queries/qualifiers";
import { SOURCES } from "utils/api/graphql/queries/sources";

import { MdFilterList } from "react-icons/md";
import {
  AdvancedFilterPanel,
  FilterChips,
  SavedFiltersBar,
} from "shared/components/AdvancedFilter";
import { NAME_SPACES } from "shared/locales/constants";
import {
  ACTIONS,
  INITIAL_VALUES,
  PROJECTS_SCOPE,
  USER_TYPES,
} from "utils/constants";
import { ADVANCED_FILTERS, LEAD_TYPES } from "utils/constants/filters";
import { setPaginationHistory } from "utils/helpers/pagination";
import ContactCard from "../../Clients/Card";
import DuplicateDrawer from "./DuplicateDrawer";
import ExportModal from "./ExportModal";
import OfferDrawer from "./Offers";
import SignProjectDrawer from "./SignProjectDrawer";
import Subscriptions from "./Subscriptions";
import ProjectsTable from "./Table";
import ScopeToggle from "./ScopeToggle";

const List = ({
  filter,
  onSearch,
  applyFilter,
  pagination,
  setPagination,
  createProject,
  actions,
  exportProjects,
  exportLoading,
  sendByEmailLoading,
  showExportModal,
  setShowExportModal,
  handleExportProjects,
  handleSendProjectsByEmail,
  duplicate,
  onDelete,
  onDetail,
  onAssign,
  isAssigning,
  setTracer,
  onChangeStatus,
  tracers,
  setTracers,
  user = {},
  scope = PROJECTS_SCOPE.MINE,
  showScopeToggle = false,
  scopeCounts = {},
  onChangeScope,
  paginationFootprint = "projectsList",
  matchStatusesWithProcess,
  advancedFilterProps,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const isSupervisor = user?.meta?.type === USER_TYPES.INTERNAL_SUPERVISOR.type;
  const isAdmin = user?.meta?.type === USER_TYPES.INSURANCE_ADMIN.type;
  const isOrganizationScope =
    showScopeToggle && scope === PROJECTS_SCOPE.ORGANIZATION;
  const canSearchByAdvisor = isSupervisor || isOrganizationScope;
  const FILTER = t("FILTER", { returnObjects: true });
  const LIST = t("LIST", { returnObjects: true });
  const PROCESS_TAGS = t("PROCESS_TAGS", { returnObjects: true });
  const ADVANCED_FILTER = t("ADVANCED_FILTER", { returnObjects: true });
  const [openSignDrawer, setOpenSignDrawer] = useState(null);
  const [createOfferDrawer, setCreateOfferDrawer] = useState(null);
  const [duplicateDrawerProject, setDuplicateDrawerProject] = useState(null);
  const [openSubscriptionDrawer, setOpenSubscriptionDrawer] = useState(null);
  const [contactId, setContactId] = useState(null);
  const [project, setProject] = useState(null);
  const [search, setSearch] = useState({});
  const [open, setOpen] = useState(false);
  const [offers, setOffers] = useState([]);
  const [status, setStatus] = useState({});
  const [resultsCount, setResultsCount] = useState(null);

  const { data: qualifiersData } = useQuery(QUALIFIERS);
  const qualifierOptions = (qualifiersData?.qualifiers?.data || []).map(
    ({ name }) => ({ label: name, value: name }),
  );

  const { data: sourcesData } = useQuery(SOURCES);
  const sourceOptions = (sourcesData?.sources?.data || []).map(({ name }) => ({
    label: name,
    value: name,
  }));

  const leadTypeOptions = Object.values(LEAD_TYPES).map((label) => ({
    label,
    value: label,
  }));

  // The advisor and supervisor filters send user ids, so the whole list has to
  // be loaded up front: filter rows and chips both resolve their labels from
  // the static selectionOptions map.
  const { data: organizationUsersData } = useQuery(graphql.queries.USERS, {
    skip: !isAdmin,
    variables: {
      take: null,
      exclude: ["telecoms", "attachments", "broker", "contact"],
      isIn: { organization: { id: [user?.organization?.id] } },
      where: {
        AND: [
          { isActive: true },
          {
            OR: [
              { meta: { type: USER_TYPES.INTERNAL_ADVISOR.type } },
              { meta: { type: USER_TYPES.INTERNAL_SUPERVISOR.type } },
              { meta: { type: USER_TYPES.BROKER_EMPLOYEE.type } },
              { meta: { type: USER_TYPES.BROKER_ADMIN.type } },
              { meta: { type: USER_TYPES.BROKER_REPRESENTATIVE.type } },
            ],
          },
        ],
      },
    },
  });

  const organizationUsers = organizationUsersData?.users?.data || [];
  const toUserOption = ({ id, firstname, lastname }) => ({
    label: `${firstname || ""} ${lastname || ""}`.trim(),
    value: id,
  });
  const advisorOptions = organizationUsers.map(toUserOption);
  const supervisorOptions = organizationUsers
    .filter(({ meta }) => meta?.type === USER_TYPES.INTERNAL_SUPERVISOR.type)
    .map(toUserOption);
  const [searchParams, setSearchParams] = useSearchParams();
  const [getProject, { data: projectFromUrl }] = useLazyQuery(
    graphql.queries.PROJECT,
  );

  useEffect(() => {
    const openContact = searchParams.get("openContact");
    const projectId = searchParams.get("projectId");
    if (!openContact) return;
    setContactId(openContact);
    setOpen(true);
    if (projectId) {
      getProject({ variables: { where: { id: projectId } } });
    } else {
      setProject(null);
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("openContact");
        next.delete("projectId");
        return next;
      },
      { replace: true },
    );
  }, [searchParams, getProject, setSearchParams]);

  useEffect(() => {
    if (projectFromUrl?.project) {
      setProject(projectFromUrl.project);
    }
  }, [projectFromUrl]);

  const didMountFiltersRef = useRef(false);
  useEffect(() => {
    if (!advancedFilterProps) return;
    if (!didMountFiltersRef.current) {
      didMountFiltersRef.current = true;
      return;
    }
    setPagination((prev) => ({ ...prev, skip: 0 }));
    if (advancedFilterProps.appliedFilters.length === 0) {
      setResultsCount(null);
    }
  }, [advancedFilterProps?.appliedFilters, setPagination]);

  const fields = [
    {
      key: "OFFER",
      icon: <MdFilterList size={16} />,
      options: tracers.map(({ name }) => name),
      mode: "tags",
      translate: FILTER.OFFER,
      variable: "offers",
    },
    {
      key: "STATUS",
      icon: <MdFilterList size={16} />,
      options: Object.keys(PROCESS_TAGS),
      mode: "tags",
      translate: FILTER.PROCESS_TAG,
      variable: "tags",
    },
  ];

  const operators = [
    { operator: "EQUAL" },
    { operator: "IS_IN" },
    { operator: "START_WITH" },
    { operator: "END_WITH" },
  ];

  const selectionOptions = {
    "statuses.status.tag": Object.keys(PROCESS_TAGS).map((key) => ({
      label: PROCESS_TAGS[key],
      value: key,
    })),
    "tracer.source.name": sourceOptions,
    "contact.fields.leadType": leadTypeOptions,
    "tracer.name": tracers.map(({ name }) => ({
      label: name,
      value: name,
    })),
    "qualification.qualifier.name": qualifierOptions,
    hasResponsible: [
      { label: LIST.TAKEN_OVER, value: "true" },
      { label: LIST.DISTRIBUTED, value: "false" },
    ],
    "author.id": advisorOptions,
    "author.supervisor.id": supervisorOptions,
  };

  const onCloseSubscription = () => {
    setOpenSubscriptionDrawer(null);
  };

  const onCloseSign = () => {
    setOpenSignDrawer(null);
  };

  const showDrawerSubscription = (id) => {
    setOpenSubscriptionDrawer(id);
  };

  const showDrawerSign = ({ id, statuses }) => {
    setOpenSignDrawer({ id, statuses });
  };

  const PAGINATION_OPTIONS = [
    { label: `10 ${LIST.FILTERS.PROJECTS}`, value: 10 },
    { label: `20 ${LIST.FILTERS.PROJECTS}`, value: 20 },
    { label: `50 ${LIST.FILTERS.PROJECTS}`, value: 50 },
    { label: `100 ${LIST.FILTERS.PROJECTS}`, value: 100 },
  ];
  return (
    <>
      <Header
        key={"header"}
        title={LIST.HEADER.TITLE}
        actions={[
          ...(actions[ACTIONS.ADD_PROJECT]
            ? [
                {
                  icon: <BiPlus size={20} />,
                  text: LIST.HEADER.CREATE.NORMAL,
                  onClick: () => setCreateOfferDrawer(true),
                },
              ]
            : []),
          ...(actions[ACTIONS.EXPORT_SUBSCRIPTIONS]
            ? [
                {
                  icon: <TbFileExport size={20} />,
                  text: LIST.HEADER.EXPORT_PROJECTS,
                  onClick: exportProjects,
                  loading: exportLoading,
                },
              ]
            : []),
        ]}
      />
      <Card type="simple">
        {showScopeToggle && (
          <ScopeToggle
            scope={scope}
            onChange={onChangeScope}
            mineLabel={LIST.SCOPE_TABS.MINE}
            allLabel={LIST.SCOPE_TABS.ALL}
            mineCount={scopeCounts.mine}
            allCount={scopeCounts.organization}
          />
        )}
        <div className="filter__area">
          <div className="search--bar">
            <Input
              placeholder={LIST.FILTERS.CLIENT_LAST_NAME}
              onChange={({ target: { value } }) =>
                setSearch((prev) => ({ ...prev, clientLastName: value }))
              }
            />
            <Input
              placeholder={LIST.FILTERS.CLIENT_FIST_NAME}
              onChange={({ target: { value } }) =>
                setSearch((prev) => ({ ...prev, clientFirstName: value }))
              }
            />
            {canSearchByAdvisor && (
              <>
                <Input
                  placeholder={LIST.FILTERS.ADVISOR_LAST_NAME}
                  onChange={({ target: { value } }) =>
                    setSearch((prev) => ({ ...prev, advisorLastName: value }))
                  }
                />
                <Input
                  placeholder={LIST.FILTERS.ADVISOR_FIST_NAME}
                  onChange={({ target: { value } }) =>
                    setSearch((prev) => ({ ...prev, advisorFirstName: value }))
                  }
                />
              </>
            )}
            <Button
              type="secondary"
              onClick={() => onSearch(search)}
              icon={<FaSearch size={14} />}
              size={"full--w"}
            >
              {LIST.FILTERS.FILTER}
            </Button>
          </div>
          <div className="filter__search">
            {advancedFilterProps && (
              <AdvancedFilterPanel
                fields={ADVANCED_FILTERS.PROJECT.fields}
                panelFilters={advancedFilterProps.panelFilters}
                setPanelFilters={advancedFilterProps.setPanelFilters}
                appliedFilters={advancedFilterProps.appliedFilters}
                onApply={advancedFilterProps.applyFilters}
                onSave={advancedFilterProps.savePreset}
                context={{
                  isSupervisor,
                  isAdmin,
                  isOrganizationScope,
                  isRingEnabled:
                    user?.organization?.config?.aircall?.enabled === true,
                }}
                translate={ADVANCED_FILTER}
                selectionOptions={selectionOptions}
                filterCount={advancedFilterProps.filterCount}
              />
            )}
            <Select
              options={PAGINATION_OPTIONS}
              value={pagination.take || INITIAL_VALUES.take}
              onChange={(value) => {
                setPaginationHistory({
                  footprint: paginationFootprint,
                  value: Math.floor(pagination.skip / value) + 1,
                  take: value,
                  count: pagination.count,
                });
                setPagination((prev) => ({ ...prev, take: value }));
              }}
            />
          </div>
        </div>
        {advancedFilterProps && advancedFilterProps.presets.length > 0 && (
          <SavedFiltersBar
            presets={advancedFilterProps.presets}
            activePresetId={advancedFilterProps.activePresetId}
            onApplyPreset={advancedFilterProps.applyPreset}
            onDeletePreset={advancedFilterProps.deletePreset}
            translate={ADVANCED_FILTER}
          />
        )}
        {advancedFilterProps &&
          advancedFilterProps.appliedFilters.length > 0 && (
            <FilterChips
              fields={ADVANCED_FILTERS.PROJECT.fields}
              appliedFilters={advancedFilterProps.appliedFilters}
              onRemoveFilter={advancedFilterProps.removeFilter}
              translate={ADVANCED_FILTER}
              selectionOptions={selectionOptions}
              resultsCount={resultsCount}
            />
          )}
      </Card>
      {contactId && (
        <ContactCard
          contactId={contactId}
          project={project}
          open={open}
          matchStatusesWithProcess={matchStatusesWithProcess}
          tracers={tracers}
          setTracers={setTracers}
          setOpen={setOpen}
        />
      )}
      <OfferDrawer
        setCreateOfferDrawer={setCreateOfferDrawer}
        createProject={createProject}
        open={createOfferDrawer}
        tracers={tracers}
      />
      <DuplicateDrawer
        open={!!duplicateDrawerProject}
        project={duplicateDrawerProject}
        onClose={() => setDuplicateDrawerProject(null)}
        duplicate={duplicate}
        setTracer={setTracer}
        tracers={tracers}
      />
      <div className="m-bottom-24" />
      <Projects
        View={ProjectsTable}
        setValues={setPagination}
        setResultsCount={setResultsCount}
        skip={pagination.skip}
        filter={{ ...filter, ...pagination }}
        fetchPolicy="network-only"
        onDuplicate={setDuplicateDrawerProject}
        onDelete={onDelete}
        onDetail={onDetail}
        onAssign={onAssign}
        isAssigning={isAssigning}
        // tracer={tracer}
        setTracer={setTracer}
        showDrawerSubscription={showDrawerSubscription}
        showDrawerSign={showDrawerSign}
        tracers={tracers}
        setContactId={setContactId}
        setProject={setProject}
        setOpen={setOpen}
        onChangeStatus={onChangeStatus}
        actions={actions}
        pagination={pagination}
        matchStatusesWithProcess={matchStatusesWithProcess}
        user={user}
        scope={scope}
        showScopeToggle={showScopeToggle}
        paginationFootprint={paginationFootprint}
      />
      {openSubscriptionDrawer && (
        <Subscriptions
          onChangeStatus={onChangeStatus}
          onClose={onCloseSubscription}
          tracers={tracers}
          project={openSubscriptionDrawer}
          user={user}
        />
      )}
      {openSignDrawer?.id && (
        <SignProjectDrawer onClose={onCloseSign} project={openSignDrawer} />
      )}
      <ExportModal
        visible={showExportModal}
        onCancel={() => setShowExportModal(false)}
        onConfirm={handleExportProjects}
        onSendByEmail={handleSendProjectsByEmail}
        exportLoading={exportLoading}
        sendByEmailLoading={sendByEmailLoading}
        baseWhere={{}}
      />
    </>
  );
};

export default List;
